import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  questionTimeLimitMs,
  type AnswerResult,
  type LeaderboardEntryWithDelta,
  type ServerMessage,
} from "@binogi-livequiz/shared";
import { Leaderboard } from "../components/Leaderboard";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import { useWebSocket } from "../lib/ws-context";
import { loadStudentSession } from "../lib/session-storage";
import { useStudentRejoin } from "../hooks/useStudentRejoin";

interface RouteState {
  sessionCode: string;
  displayName: string;
  questionIndex: number;
  correctOids: string[];
  results: AnswerResult[];
  leaderboard: LeaderboardEntryWithDelta[];
  mySelection: string[] | null;
  availableLanguages?: string[];
}

export default function PlayBetween() {
  const navigate = useNavigate();
  const location = useLocation();
  const ws = useWebSocket();
  const { t } = useTranslation();
  const state = location.state as RouteState | null;

  // Reload-safe rejoin (and mid-question socket drops) — see the hook.
  // `myName` is the name the *server* knows us by: a rejoin can be answered
  // with a different one (name collision), and every lookup below resolves
  // our row by name. Using the stale route-state name here showed a
  // renamed student score 0, "Wrong!" and no leaderboard highlight.
  const { displayName: myName, superseded } = useStudentRejoin(state);

  const mine = useMemo(() => {
    if (!state) return null;
    return state.leaderboard.find((e) => e.displayName === myName);
  }, [state, myName]);

  const rank = useMemo(() => {
    if (!state) return null;
    const idx = state.leaderboard.findIndex((e) => e.displayName === myName);
    return idx >= 0 ? idx + 1 : null;
  }, [state, myName]);

  // Drive the "Correct!" banner from the server's per-question delta,
  // NOT the local mySelection-vs-correctOids comparison. A student on
  // flaky wifi can tap the right answer but have their SUBMIT_ANSWER
  // arrive past the LATE_ANSWER_GRACE window — the server rejects it,
  // no points are awarded, but a local comparison would still light
  // up "Correct!" with a score of 0. Trust the leaderboard delta:
  // it's the same number we render under "+points gained".
  const wasCorrect = useMemo(() => {
    if (!state || !state.mySelection || state.mySelection.length === 0)
      return null;
    return (mine?.delta ?? 0) > 0;
  }, [state, mine]);

  useEffect(() => {
    const m = ws.lastMessage;
    if (!m || !state) return;
    handle(m);
    function handle(msg: ServerMessage) {
      if (msg.type === "QUESTION_START") {
        ws.consume();
        navigate("/play/question", {
          state: {
            sessionCode: state!.sessionCode,
            displayName: myName,
            questionIndex: msg.questionIndex,
            totalQuestions: msg.totalQuestions,
            timeLimitMs: questionTimeLimitMs(msg),
            question: msg.question,
            startedAt: Date.now(),
            availableLanguages: state!.availableLanguages,
          },
        });
      } else if (msg.type === "SESSION_FINISHED") {
        ws.consume();
        navigate("/play/finished", {
          state: {
            displayName: myName,
            finalLeaderboard: msg.finalLeaderboard,
            availableLanguages: state!.availableLanguages,
          },
        });
      }
    }
  }, [myName, navigate, state, ws]);

  if (!state) {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-white/70">{t("play.no_round")}</p>
          <button
            type="button"
            onClick={() => navigate("/join")}
            className="mt-4 px-4 py-2 bg-brand-purple text-white rounded-lg"
          >
            {t("play.back_to_join")}
          </button>
        </div>
      </main>
    );
  }

  const headline =
    wasCorrect === null
      ? t("play.between.times_up")
      : wasCorrect
        ? t("play.between.correct")
        : t("play.between.wrong");
  const headlineClass =
    wasCorrect === null
      ? "text-brand-amber"
      : wasCorrect
        ? "text-brand-good"
        : "text-brand-bad";
  const myScore = mine?.score ?? 0;
  const myDelta = mine?.delta ?? 0;

  const langPickerCodes =
    state.availableLanguages ?? loadStudentSession()?.availableLanguages;

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-10 max-w-md mx-auto">
      <ScreenHeader short wordmark="sm" available={langPickerCodes} />

      {superseded && (
        <p
          role="status"
          className="mb-4 rounded-xl border border-brand-amber/50 bg-brand-amber/20 px-4 py-3 text-center text-sm font-semibold"
        >
          {t("play.superseded")}
        </p>
      )}

      <section className="text-center mb-8">
        <h1 className={`text-4xl md:text-5xl font-extrabold ${headlineClass} animate-pop`}>
          {headline}
        </h1>
        {myDelta > 0 && (
          <p className="mt-3 text-brand-good font-semibold text-xl">
            {t("play.between.points_gained", { count: myDelta })}
          </p>
        )}
      </section>

      <section className="bg-brand-ink2 rounded-2xl p-5 mb-6 text-center">
        <div className="text-sm text-white/60">{t("play.between.your_score")}</div>
        <div className="text-4xl font-extrabold tabular-nums">{myScore}</div>
        {rank !== null && (
          <div className="mt-1 text-sm text-white/70">
            {t("play.between.your_rank", { rank })}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-semibold mb-3">{t("play.between.top_n", { count: 5 })}</h2>
        <Leaderboard
          entries={state.leaderboard.slice(0, 5)}
          highlight={myName}
          compact
        />
      </section>

      <p className="mt-8 text-center text-white/50 text-sm">
        {t("play.between.waiting_next")}
      </p>
    </main>
  );
}
